import { EmberRosterRole, Entity, TileSize } from "game-common/models"
import { Callback, chunkSentence } from "game-common/util"
import { SPEECH_HELD_MS, SPEECH_WRAP_COLUMNS, speechReadingMs } from "game-common/speech_timing"
import { BitmapText, Container, Graphics, LINE_JOIN, Point, Text, TextMetrics } from "pixi.js"

import { EntityRenderMeta } from "../entity_rendering_manager"
import { createEmberRoleGlyph } from "../gui/ember_role_icons"
import { Fader } from "../gui/fader"
import { BaseRenderable, SpeechOptions } from "./base_renderable"
import { BasicSprite } from "./basic_sprite"
import { createButton } from "../gui/button_factory"
import { UiButton } from "../gui/ui_button"
import { BUTTON_SIZES, HUD_PANEL_FILL, HUD_PANEL_RIM, HUD_PANEL_TEXT } from "../gui/ui_tokens"
import { GOLD, RIM_LIGHT, TIMBER_EDGE } from "../gui/build_board"
import { TextRenderer } from "../text/text_renderer"
import { DyanmicTextContainer } from "../gui/dynamic_text_container"
import { SpeechHoldButtons } from "../gui/speech_hold"
import {
    BubblePlacement, BubbleRect, BubbleSide, BubbleSlot, DEFAULT_PLACEMENT, LaidOutBubble, SpeechBubbleLayout,
} from "./speech_bubble_layout"

// Chat speech-bubble look (see spokeImpl). It is the camp's cut-timber panel -- the HUD_PANEL plate
// with build_board's chamfer and an inked bronze rim -- because it used to be the last cool-slate
// panel on screen: a blue-grey rounded rect over warm wood, beside a roster bubble (roster_speech.ts)
// that already said the same lines on the warm plate.
const BUBBLE_PAD_X = 12
const BUBBLE_PAD_Y = 10
const BUBBLE_SPEAKER_OFFSET_Y = 8
// Cut corners, not a radius: a radius is a desktop dialog, a chamfer is a sawn board.
const BUBBLE_CHAMFER = 5
// Near-solid. At 0.9 the bark of a tree behind the bubble striped through the words.
const BUBBLE_FILL = HUD_PANEL_FILL
const BUBBLE_FILL_ALPHA = 0.97
// The rim is two strokes down the same path: a wide dark one, then a thin bronze one over its middle,
// so the bronze is inked on both sides and the outline stays seamless around the tail.
const BUBBLE_EDGE_WIDTH = 3.5
const BUBBLE_EDGE_ALPHA = 0.95
const BUBBLE_RIM_WIDTH = 1.25
const BUBBLE_MITER_LIMIT = 3 // bevels the tail's tip rather than spiking it
const BUBBLE_TOP_LIGHT_ALPHA = 0.2
// Dropped straight down. The old diagonal offset showed as a second, ghost tail beside the real one.
const BUBBLE_SHADOW_Y = 3
const BUBBLE_SHADOW_ALPHA = 0.38
const BUBBLE_TEXT_COLOR = HUD_PANEL_TEXT
// Where the body sits beside its speaker, in HUD units. At rest it is up and to the right of the head
// (BUBBLE_X is its left edge); when the layout moves it to make room for another speaker's bubble
// (see speech_bubble_layout.ts) it can slide, rise, or mirror about the head's centre onto the left.
// HEAD_CENTER_X is measured rather than taken from GLYPH_X: on an LPC body the hair spans about
// -9..37 in these units, and the role glyph -- nominally at 18 -- is drawn centred at ~14.
const BUBBLE_X = TileSize
const HEAD_CENTER_X = 14
// The tail, in bubble-local units, its base on the bottom edge. The tail reaches back LEFT to the
// head and its tip lands OVER the head, 8 units right of its centre (mirrored on the other side). It
// first stopped at +6 from a bubble at 1.4 tiles, which pointed at the grass beside the speaker; then
// at the head's right EDGE, which with a neighbour a tile away is right beside THEIR head -- the two
// tails met in the gap between the speakers and nobody could tell whose line was whose.
const TAIL_BASE_LEFT_X = 6
const TAIL_BASE_RIGHT_X = 26
const TAIL_TIP_X = HEAD_CENTER_X + 8 - BUBBLE_X
const TAIL_DROP = 13 // tip, below the bubble's bottom edge
// A bubble the layout DROPS (a speaker near the top of the screen, see speech_bubble_layout.ts) comes
// down beside the head, past where a tail off its bottom edge could reach the tip. Below this much
// drop left for it, the tail comes out of the speaker-facing side instead, SIDE_TAIL_HALF either side
// of the tip's height, pointing across at the head -- and always at least SIDE_TAIL_LEN out, because
// a drop that also had to slide in from a screen edge can bring the body right up to the tip, and a
// bubble with no tail at all is only tied to its speaker by standing next to them.
const MIN_TAIL_DROP = 5
const SIDE_TAIL_HALF = 6
const SIDE_TAIL_LEN = 6
// The speaker's head as the layout keeps other bubbles off it, relative to the tail tip (which sits
// level with the bottom of the role glyph, just above the hair). Measured on an LPC body: hair
// -9..37 across, chin ~46 below the tip. The box starts 8 ABOVE the tip -- the glyph's lower half;
// only a DROPPED bubble is kept off it (see speech_bubble_layout.ts).
const HEAD_HALF_W = 23
const HEAD_ABOVE_TIP = 8
const HEAD_BELOW_TIP = 45
// How long a bubble takes to rise or settle when the layout moves it.
const BUBBLE_MOVE_MS = 160
// Scratch points for bubbleSlot()'s transform reads, so the per-frame layout allocates nothing.
const LAYOUT_ORIGIN = new Point(0, 0)
const LAYOUT_UNIT = new Point(1, 1)
const layoutOriginOut = new Point()
const layoutUnitOut = new Point()

// The "Next" button on a HELD bubble (see spoke): a line the player dismisses themselves rather
// than one that fades on a timer. It hangs off the bottom-right inside edge of the panel, on its own
// row, so it can never sit over the words however they wrap.
const NEXT_BUTTON_W = 52
const NEXT_BUTTON_H = BUTTON_SIZES.small.height
const NEXT_BUTTON_GAP = 7 // clear air between the last line of text and the button row

// A held line stops the scene behind it until it is dismissed, so the button presses ITSELF after
// this long: a player who wanders off, misses the button, or can't reach it (a bubble drawn half off
// the screen) must not be able to strand the ember tutorial for good. The countdown bar along the
// bottom of the button is the only cue, and it is deliberately quiet -- the button is still the way
// you're meant to get through a conversation, not a thing to race.
const NEXT_BUTTON_AUTO_MS = SPEECH_HELD_MS
// Redraw cadence of that bar. Coarse on purpose: the bar is ~42px wide, so a finer tick cannot move
// it a whole pixel, and a live bubble has no business churning the GPU on a budget machine.
const NEXT_PROGRESS_TICK_MS = 250
const NEXT_PROGRESS_H = 2
const NEXT_PROGRESS_ROW = 4 // the bar plus its clearance from the button's bottom border
const NEXT_PROGRESS_INSET = 5 // keeps the bar clear of the chamfer (UiButton cuts min(5, h / 4))
const NEXT_PROGRESS_COLOR = GOLD
const NEXT_PROGRESS_ALPHA = 0.6

// Ceiling on the per-entity backlog of lines waiting behind a held bubble. A player who walks away
// mid-conversation never presses Next, and the speaker (the ember Lord, mid-intro) keeps talking;
// without a cap that queue grows for as long as the run lasts. Dropping the OLDEST line keeps the
// most recent, most relevant ones.
const MAX_QUEUED_LINES = 8

// Compact segmented HP meter drawn beneath the name (variant B) -- replaces the old "|" pipe text.
// Colours mirror the roster HP bar (ember_roster drawHpBar) so the world and roster read the same.
const HP_BAR_W = 54
const HP_BAR_H = 7
const HP_SEGMENTS = 6
const HP_SEG_GAP = 2
const HP_CASE_PAD = 2
const HP_COLOR_FULL = 0x6fe06f
const HP_COLOR_WARN = 0xffd23f
const HP_COLOR_CRIT = 0xff5c5c

// Floating role glyph (crown / shield / axe) shown above an ember-camp NPC's head. It
// rests just above the head and lifts on hover to make room for the name + HP, then drops back down.
// The Y values are the tunable bits (like the HP-bar alignment was).
const GLYPH_SIZE = 15
const GLYPH_X = 18 // centred over the NPC (tunable)
const GLYPH_Y = 20 // rests just above the head; the glyph fades out on hover (it does not move)
const GLYPH_FADE_MS = 130
// Players already have a ground ring, so only their roster card carries a badge.

// Draw order of the per-entity widgets inside the HUD. These only take effect because the HUD turns
// on sortableChildren in the constructor -- without it pixi paints in addChild order, which put the
// role glyph and the sigil (added later, on demand) on top of the chat bubble.
const Z_TAG = 0
const Z_LABEL = 10 // name + HP meter
const Z_SIGIL = 20
const Z_ROLE_GLYPH = 30
const Z_POINTER = 40
const Z_CHAT_BUBBLE = 100000 // a bubble always reads over every other widget of its entity
// Every entity's HUD is a separate child of the world-overlay surface, so ordering the bubble within
// its own HUD isn't enough -- a neighbour's nameplate/glyph would still paint over it. The speaking
// entity's whole overlay item is lifted above the resting ones for as long as its bubble is up, and
// among the speakers, by the paint rank the bubble layout hands out (newest line on top -- see
// bubblePaintRanks). Without that, two lifted items tied and pixi fell back to the order the
// ENTITIES were created in, so a fresh line could open underneath an old one.
const Z_OVERLAY_RESTING = 0
const Z_OVERLAY_SPEAKING = 1000

// interface Job {
//     run: (done: Callback<void>) => void
// }

// class JobQueue {
//     private queue: Job[] = []
//     private running: boolean = false

//     constructor() {
//         //
//     }

//     add = (callback: Job) => {
//         if (this.queue.length > 2) {
//             this.queue.shift()
//         }
//         this.queue.push(callback)
//         if (!this.running) {
//             this.run()
//         }
//     }

//     private run = () => {
//         this.running = true
//         const callback = this.queue.shift()
//         if (callback) {
//             callback.run(() => this.run())
//         } else {
//             this.running = false
//         }
//     }
// }

export class AvatarHud extends BaseRenderable implements LaidOutBubble {
    id: string
    idText: Text
    hpBar: Graphics
    currentName: string = ""
    currentLevel: number
    currentColor: number
    currentHp: number = 0
    currentHpMax: number = 0

    roleGlyph?: Text | Graphics
    currentRole: EmberRosterRole | null = null
    glyphAnimInterval: any

    alwaysShowLabel: boolean
    suppressDetailHover: boolean

    tag: Graphics

    idleTimeout: any
    blinkingInterval: any

    chatBubble: Graphics
    chatBubbleHorizontalSpacer: Graphics
    bubbleText: DyanmicTextContainer
    chatBubbleTimeout: any
    // The fade-out of the line on screen, kept so that a line said mid-fade can stop it. Left
    // running, it drained the NEW line's bubble and then wiped it in its release.
    private chatBubbleFader?: Fader
    damageInterval: any
    // The drawn bubble's size and resting y, kept so the layout can move it without re-measuring.
    private bubbleW = 0
    private bubbleH = 0
    private bubbleRestY = 0
    // Where the layout has put the bubble: `placement` is what is drawn, `placementTarget` where it
    // is heading. They differ only while a move is easing (bubbleMoveEase).
    private placement: BubblePlacement = DEFAULT_PLACEMENT
    private placementTarget: BubblePlacement = DEFAULT_PLACEMENT
    private bubbleMoveEase?: { from: BubblePlacement; to: BubblePlacement; startedAt: number }
    // Set when a line opens: its first placement snaps rather than eases, since nobody has seen it.
    private placementFresh = false
    // When the line on screen opened, as the layout counts it; 0 while no line is open.
    private bubbleOrder = 0

    // --- Held speech (see spoke) -------------------------------------------------------------
    // The "Next" button, built once and reused; only parented/registered while a held line is
    // waiting on it.
    nextButton?: UiButton
    // The countdown bar drawn under the label (see drawNextProgress). Its own Graphics so a tick
    // repaints two pixels of bar instead of the whole button.
    private nextButtonProgress?: Graphics
    // The self-press that keeps a held line from stalling the scene, and the redraw that shows it
    // coming. Both live exactly as long as an armed button -- see armNextAutoAdvance.
    private nextAutoTimeout: any
    private nextProgressInterval: any
    private nextArmedAt: number = 0
    // Width the bar was last drawn at, so a tick that wouldn't move it draws nothing.
    private nextProgressWidth: number = -1
    // True from the moment a held line lands until its Next is pressed. While it is set, everything
    // this entity says queues rather than replacing what's on screen.
    holding: boolean = false
    // Lines waiting their turn behind the held bubble, oldest first. They inherit the hold, so the
    // player cycles through the whole backlog with the same button, and each carries its OWN ack --
    // the speaker is told about the line the player actually just read, not the one at the front.
    private speechQueue: { message: string; onDismissed?: Callback<void> }[] = []
    // Whether the line currently on screen was drawn instantly (PiP focus) -- carried so a queued
    // line is shown the same way.
    private lastInstant: boolean = false
    // The line on screen while `holding`, kept only to recognize a repeat of it (see spoke).
    private currentHeldMessage?: string
    // Completion callback of the line being held, run when its bubble finally closes.
    private holdDone?: Callback<void>
    // The held line's ack -- see SpeechOptions.onDismissed. Fired by the Next press, not by the
    // bubble closing: the player reading the line is the event the speaker is waiting on.
    private heldDismissedCallback?: Callback<void>

    pointerIcon: Graphics
    pointerIconPulseInterval: any
    pointerIconAngleInterval: any
    sigil: Graphics
    // chatQueue: JobQueue = new JobQueue()

    speaking: boolean

    constructor(
        color = 0xff00ff,
        renderMeta: EntityRenderMeta,
        parentArea: BaseRenderable,
        suppressDetailHover?: boolean,
    ) {
        super()
        this.parentArea = parentArea
        this.currentColor = color

        // Paint the HUD's widgets by zIndex (see the Z_* constants) rather than by addChild order.
        this.sortableChildren = true

        this.alwaysShowLabel = renderMeta.alwaysShowLabel

        this.idText = new TextRenderer().role("entityName").color(0xffffff).render() as Text
        // No backing plate (variant B): give the name a crisp black outline + soft shadow so it stays
        // legible over busy terrain.
        const nameStyle = this.idText.style
        nameStyle.stroke = "#000000"
        nameStyle.strokeThickness = 3
        nameStyle.lineJoin = "round"
        nameStyle.dropShadow = true
        nameStyle.dropShadowColor = "#000000"
        nameStyle.dropShadowAlpha = 0.55
        nameStyle.dropShadowBlur = 2
        nameStyle.dropShadowAngle = Math.PI / 2
        nameStyle.dropShadowDistance = 1

        // HP as a compact segmented meter drawn below the name (replaces the old "|" pipe text).
        this.hpBar = new Graphics()

        this.suppressDetailHover = suppressDetailHover
        const startVisible = !suppressDetailHover ? this.alwaysShowLabel : false
        this.idText.visible = startVisible
        this.hpBar.visible = startVisible
        this.idText.alpha = 1

        const tag = new Graphics()
        tag.beginFill(0xffffff)
        tag.lineStyle(1, 0xffffff)
        tag.drawCircle(-8, 8, 6)
        tag.endFill()
        tag.zIndex = Z_TAG
        this.addChild(tag)
        tag.visible = false
        tag.alpha = 0.7
        this.tag = tag

        this.idText.zIndex = Z_LABEL
        this.hpBar.zIndex = Z_LABEL
        this.addChild(this.idText)
        this.addChild(this.hpBar)

        this.update(3, 3)
        this.calcLabel()

        this.chatBubble = new Graphics()
        this.chatBubble.visible = true
        this.chatBubble.x = 0
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.addChild(this.chatBubble)

        const chatBubbleHorizontalSpacer = new Graphics()
        this.chatBubble.addChild(chatBubbleHorizontalSpacer)

        const bubbleText = this.makeBubbleText()
        this.chatBubble.addChild(bubbleText)
        this.chatBubble.visible = false

        const pointer = new BasicSprite({
            name: "icon-arrow.png",
            color: 0xffffff,
            scale: 1.0,
        })
        pointer.visible = false
        pointer.zIndex = Z_POINTER

        this.addChild(pointer)
        this.pointerIcon = pointer
    }

    makeBubbleText = () => {
        const bubbleText = new DyanmicTextContainer({
            // progressive is set per-utterance in spokeImpl (typewriter on the main view, instant in
            // the PiP focus), so this is just the default.
            progressive: true,
            // The styled panel drawn in spokeImpl is the bubble background now; the old inner
            // text-hugging backdrop just muddied it with a second dark rect, so it's disabled.
            backdrop: false,
        })
        bubbleText.x = BUBBLE_PAD_X
        bubbleText.y = BUBBLE_PAD_Y
        this.bubbleText = bubbleText

        return bubbleText
    }

    updateSigil(sigil?: string) {
        // remove existing sigil
        if (this.sigil) {
            this.sigil.parent?.removeChild(this.sigil)
        }

        if (sigil) {
            this.sigil = new BasicSprite({
                name: sigil,
                scale: 1.8,
                color: 0xffffff,
            })
            this.sigil.x += this.sigil.width * 0.3
            this.sigil.y = -3
            this.sigil.zIndex = Z_SIGIL
            this.addChild(this.sigil)
        }
    }

    // Show/replace the floating role glyph above the head (or remove it when the entity has no role).
    updateRole = (role: EmberRosterRole | null) => {
        if (this.currentRole === role) {
            return
        }
        this.currentRole = role

        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (this.roleGlyph) {
            this.removeChild(this.roleGlyph)
            if (this.roleGlyph instanceof Text) this.roleGlyph.destroy({ texture: true, baseTexture: true })
            else this.roleGlyph.destroy()
            this.roleGlyph = undefined
        }

        if (role && role !== "player") {
            const glyph = createEmberRoleGlyph(role, GLYPH_SIZE)
            glyph.x = GLYPH_X
            glyph.y = GLYPH_Y
            // Hidden while the label is showing (hovered / always-on), else visible above the head.
            const hidden = this.idText.visible || this.alwaysShowLabel
            glyph.alpha = hidden ? 0 : 1
            glyph.visible = !hidden
            glyph.zIndex = Z_ROLE_GLYPH
            this.addChild(glyph)
            this.roleGlyph = glyph
        }
    }

    // Fade the glyph in/out -- it hides on hover so the name/HP take its place, and fades back when
    // the pointer leaves. Runs a short self-clearing interval only while fading, so there's no
    // per-frame timer sitting idle.
    private fadeGlyphTo = (targetAlpha: number) => {
        const glyph = this.roleGlyph
        if (!glyph) {
            return
        }
        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (targetAlpha > 0) {
            glyph.visible = true // fade-in from a hidden glyph must start visible
        }
        const startAlpha = glyph.alpha
        const delta = targetAlpha - startAlpha
        if (Math.abs(delta) < 0.02) {
            glyph.alpha = targetAlpha
            glyph.visible = targetAlpha > 0
            return
        }
        const stepMs = 16
        let elapsed = 0
        this.glyphAnimInterval = setInterval(() => {
            elapsed += stepMs
            const t = Math.min(1, elapsed / GLYPH_FADE_MS)
            glyph.alpha = startAlpha + delta * t
            if (t >= 1) {
                glyph.alpha = targetAlpha
                glyph.visible = targetAlpha > 0
                clearInterval(this.glyphAnimInterval)
                this.glyphAnimInterval = undefined
            }
        }, stepMs)
    }

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color
        if (this.tag) {
            this.tag.tint = color
        }
    }

    updateLevel = (level: number) => {
        if (this.currentLevel === level) {
            return
        }
        this.currentLevel = level
        this.calcLabel()
    }

    updateName = (name: string) => {
        if (name !== this.currentName) {
            this.currentName = name
            this.calcLabel()
        }
    }

    update = (hp: number, hpMax: number) => {
        if (this.currentHp !== hp) {
            this.currentHp = hp
            this.calcLabel()
        }
        if (this.currentHpMax !== hpMax) {
            this.currentHpMax = hpMax
            this.calcLabel()
        }
    }

    private calcLabel = () => {
        let name = this.currentName || ""
        const max = 15
        if (name.length > max) {
            name = name.substring(0, max - 3) + "..."
        }
        const level: string = !!name && this.currentLevel > 0 ? `Lv ${this.currentLevel}` : ""

        this.idText.y = 0
        this.idText.text = (name + "  " + level).trim()

        // Left-align the HP meter to the name's left edge, just below it.
        this.hpBar.x = this.idText.x
        this.hpBar.y = this.idText.y + this.idText.height + 2
        this.drawHpBar()
    }

    private drawHpBar = () => {
        const g = this.hpBar
        g.clear()
        if (this.currentHpMax < 1) {
            return
        }

        const ratio = Math.max(0, Math.min(1, this.currentHp / this.currentHpMax))
        const color = ratio < 0.4 ? HP_COLOR_CRIT : ratio < 0.7 ? HP_COLOR_WARN : HP_COLOR_FULL

        const innerW = HP_BAR_W - HP_CASE_PAD * 2
        const segW = (innerW - HP_SEG_GAP * (HP_SEGMENTS - 1)) / HP_SEGMENTS
        const segH = HP_BAR_H - HP_CASE_PAD * 2
        // Keep at least one segment lit while alive so a near-dead entity never reads as empty.
        const lit = this.currentHp > 0 ? Math.max(1, Math.round(ratio * HP_SEGMENTS)) : 0

        // Dark casing
        g.beginFill(0x000000, 0.72)
        g.drawRoundedRect(0, 0, HP_BAR_W, HP_BAR_H, 2)
        g.endFill()

        // Notched segments
        for (let i = 0; i < HP_SEGMENTS; i++) {
            const on = i < lit
            g.beginFill(on ? color : 0xffffff, on ? 1 : 0.1)
            g.drawRoundedRect(HP_CASE_PAD + i * (segW + HP_SEG_GAP), HP_CASE_PAD, segW, segH, 1)
            g.endFill()
        }
    }

    addTag() {
        this.tag.visible = true
        this.tag.tint = this.currentColor
    }

    removeTag() {
        this.tag.visible = false
    }

    // Raise/lower this entity's world-overlay item so a live bubble clears the HUDs of neighbouring
    // entities (their nameplates and role glyphs are siblings of this HUD, not children of it), and
    // sits in its paint rank among the other speakers'.
    private liftOverlay = (speaking: boolean, paintRank: number = 0) => {
        const overlayItem = this.parent
        if (!overlayItem) {
            return
        }
        const z = speaking ? Z_OVERLAY_SPEAKING + paintRank : Z_OVERLAY_RESTING
        // Only on a change: assigning zIndex marks the whole overlay surface for a re-sort.
        if (overlayItem.zIndex !== z) {
            overlayItem.zIndex = z
        }
    }

    // A line this entity just said. See SpeechOptions for what the flags mean.
    //
    // A HELD line's bubble waits for a "Next" press instead of fading on a timer sized to the word
    // count -- players were losing the ember Lord's intro to that timer. The press also calls the
    // line's `onDismissed`, which is how the SPEAKER gets to pace itself off the player's reading
    // speed rather than its own clock. If nobody presses, the button presses itself after
    // NEXT_BUTTON_AUTO_MS, so handing the pacing to the player can never stall a scene outright.
    //
    // Anything this entity says while a held line is up QUEUES behind it and is shown, in order, as
    // the player works through the backlog with the same button, so no line can be shouldered off
    // the screen before it has been read. A speaker that waits for the ack keeps that queue empty by
    // construction; it is the safety net for one that doesn't (and for a line from somewhere else
    // entirely, mid-conversation). Only this entity's lines queue -- every speaker has its own HUD.
    spoke = (message: string, options?: SpeechOptions) => {
        if (options?.autoAdvance) {
            this.releaseHeldSpeech()
        }
        const instant = !!options?.instant
        const hold = !!options?.hold
        const onDismissed = options?.onDismissed

        if (this.holding) {
            // Don't stack a line that's already on screen or already waiting -- a nudge the script
            // re-asks on a timer ("The wood. Onto the fire.") would otherwise become a stack of
            // identical bubbles for the player to click through.
            const lastQueued = this.speechQueue[this.speechQueue.length - 1]
            if (
                message === lastQueued?.message ||
                (!this.speechQueue.length && message === this.currentHeldMessage)
            ) {
                return
            }
            this.speechQueue.push({ message, onDismissed })
            while (this.speechQueue.length > MAX_QUEUED_LINES) {
                this.speechQueue.shift()
            }
            // The button carries the backlog count, so the player can see there's more behind it.
            this.drawNextButton()
            return
        }

        this.showLine(message, instant, hold, onDismissed)
    }

    // Put one line on screen, replacing whatever is there. The queue is handled by spoke(); by the
    // time we get here this line is the one to show.
    private showLine = (message: string, instant: boolean, hold: boolean, onDismissed?: Callback<void>) => {
        if (this.speaking) {
            this.bubbleText.wipe()
            this.bubbleText.parent.removeChild(this.bubbleText)

            this.bubbleText = this.makeBubbleText()
            this.chatBubble.addChild(this.bubbleText)
        }

        this.speaking = true
        this.holding = hold
        this.lastInstant = instant
        this.currentHeldMessage = hold ? message : undefined
        this.heldDismissedCallback = hold ? onDismissed : undefined
        this.hideNextButton()
        this.spokeImpl(message, instant, hold, () => {
            this.speaking = false
        })
    }

    // How many of this entity's lines are waiting behind the held one. Exposed for tests and for the
    // button's own label.
    queuedSpeechCount = () => this.speechQueue.length

    // Take a held bubble down WITHOUT the player pressing "Next", because the conversation it
    // belonged to has been called off -- the ember tutorial's Skip. The backlog goes with it (those
    // lines are the rest of a scene nobody is playing now), and without this the sign-off that
    // follows a skip would queue behind the very button the player just declined, inherit its hold,
    // and sit there unread.
    //
    // The speaker is deliberately NOT acked: nothing here was read, and a script pacing itself off
    // the ack falls back to its own timeout, exactly as it does for a bubble that was never drawn.
    releaseHeldSpeech = () => {
        if (!this.holding && !this.speechQueue.length) {
            return
        }
        this.speechQueue = []
        this.holding = false
        this.currentHeldMessage = undefined
        this.heldDismissedCallback = undefined
        this.hideNextButton()
        this.closeBubble()
    }

    // "Next" pressed -- by the player, or by the button's own countdown running out. Tells the
    // speaker it may go on, then shows the next line in the backlog, or closes the bubble when there
    // is none.
    advanceHeldSpeech = () => {
        if (!this.holding) {
            return
        }
        this.holding = false
        this.currentHeldMessage = undefined
        this.hideNextButton()

        // Ack the line the player just read BEFORE showing the next one: a speaker waiting on this
        // is holding its own tongue until it arrives.
        const dismissed = this.heldDismissedCallback
        this.heldDismissedCallback = undefined
        dismissed?.()

        const next = this.speechQueue.shift()
        if (next) {
            // Queued lines inherit the hold: the player has taken over the pacing of this
            // conversation, so every line of it waits for them.
            this.showLine(next.message, this.lastInstant, true, next.onDismissed)
            return
        }

        this.closeBubble()
    }

    // Cancel a pending or running fade-out without its release: whoever calls this is about to put
    // the bubble back up, or take it down itself.
    private stopBubbleFade = () => {
        if (this.chatBubbleTimeout) {
            clearTimeout(this.chatBubbleTimeout)
            this.chatBubbleTimeout = undefined
        }
        this.chatBubbleFader?.stop(true)
        this.chatBubbleFader = undefined
    }

    spokeImpl = (message: string, instant: boolean, hold: boolean, done: Callback<void>) => {
        this.stopBubbleFade()
        this.hpBar.visible = false
        this.idText.visible = false
        // A chat bubble takes over the label area; the glyph returns to its resting visible state.
        this.fadeGlyphTo(1)

        const sentences = chunkSentence(message, SPEECH_WRAP_COLUMNS)

        // Size the bubble to the ACTUAL rendered text rather than a per-char estimate -- the old
        // estimate under-sized the panel, so text crowded the right/top edges. Measure the wrapped
        // lines with the real bubble font and wrap the panel around them with uniform padding.
        const probe = new TextRenderer().role("speech").strokeThickness(0).render() as Text
        const metrics = TextMetrics.measureText(sentences.join("\n"), probe.style as any)
        probe.destroy({ texture: true, baseTexture: true })
        // A held line needs a row at the bottom for its Next button -- grow the panel around it the
        // same way the text grows it, so the button is always BELOW the words and never over them,
        // however they wrapped. (Same lesson as the tutorial bar: buttons beside server-authored
        // copy only fits until the copy gets a few words longer.)
        const textH = Math.ceil(metrics.height)
        const buttonRowH = hold ? NEXT_BUTTON_GAP + NEXT_BUTTON_H : 0
        const minW = hold ? NEXT_BUTTON_W + BUBBLE_PAD_X * 2 : 46
        const bubbleW = Math.max(minW, Math.ceil(metrics.width) + BUBBLE_PAD_X * 2)
        const bubbleH = textH + buttonRowH + BUBBLE_PAD_Y * 2

        this.bubbleText.x = BUBBLE_PAD_X
        this.bubbleText.y = BUBBLE_PAD_Y

        // The HUD is parented to the entity's click area, whose origin is just below the sprite's
        // top edge (see LpcComposite/LpcLayered). Keep the tail tip at the bottom of that sprite's
        // upper half; as the panel grows, move its top upward instead of letting the tail drift
        // down toward the speaker's feet.
        const spriteUpperHalfY = Math.max(0, this.parentArea.height / 2 - TileSize * 0.15)
        const wasShowing = this.chatBubble.visible
        this.bubbleW = bubbleW
        this.bubbleH = bubbleH
        this.bubbleRestY = spriteUpperHalfY - (bubbleH + TAIL_DROP) + BUBBLE_SPEAKER_OFFSET_Y
        if (!wasShowing) {
            // A bubble opening from nothing starts in its home spot; one replacing a line already up
            // keeps the spot that line had found, so a speaker's next line doesn't jump.
            this.placement = DEFAULT_PLACEMENT
            this.placementTarget = DEFAULT_PLACEMENT
        } else if (this.bubbleMoveEase) {
            this.placement = this.placementTarget
        }
        this.bubbleMoveEase = undefined
        this.placementFresh = true

        this.chatBubble.alpha = 1.0
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.drawBubbleFrame()
        this.bubbleOrder = SpeechBubbleLayout.opened(this)
        // Newest on top until the next layout pass ranks it properly. The order, not the open count:
        // a speaker's next line keeps the count the same, and would tie with the newest bubble.
        this.liftOverlay(true, this.bubbleOrder)

        this.chatBubble.visible = true

        this.bubbleText.progressive = !instant
        this.bubbleText.wipe()
        sentences.forEach(sentence => {
            // Warm ink, no outline: the default black stroke is for text over the open world, and on
            // a solid panel it only thickens the letters.
            this.bubbleText.append2(sentence, {
                role: "speech",
                color: BUBBLE_TEXT_COLOR,
                strokeThickness: 0,
            })
        })

        const scheduleFade = () => {
            this.chatBubbleTimeout = setTimeout(() => {
                this.chatBubbleTimeout = undefined
                const fader: Fader = new Fader(this.chatBubble, {
                    releaseCallback: () => {
                        this.chatBubbleFader = undefined
                        this.bubbleText.wipe()
                        this.chatBubble.visible = false
                        this.chatBubble.alpha = 1.0
                        this.bubbleClosed()

                        done()
                    },
                })
                this.chatBubbleFader = fader
                fader.update()
            }, speechReadingMs(message))
        }

        // A held line hands the dismissal to the player: arm the button once the words have all
        // arrived (no point offering "Next" over a half-typed sentence, nor starting its countdown
        // while they're still appearing) and schedule no fade at all.
        const finish = hold
            ? () => {
                  this.holdDone = done
                  this.showNextButton(bubbleW, bubbleH)
              }
            : scheduleFade

        if (instant) {
            // PiP focus: the line is already fully drawn (non-progressive above), so just fade — no
            // typewriter, whose progressive-reveal objects flicker when rendered into the PiP texture.
            finish()
        } else {
            this.bubbleText.playback(finish)
        }
    }

    // Take the bubble down for good: no fade, because the player has just told us they're done with
    // it and a dismissal that lingers reads as an unresponsive button.
    private closeBubble = () => {
        this.stopBubbleFade()
        this.bubbleText.cleanup()
        this.bubbleText.wipe()
        this.chatBubble.visible = false
        this.chatBubble.alpha = 1.0
        this.bubbleClosed()

        const done = this.holdDone
        this.holdDone = undefined
        done?.()
    }

    // The bubble is off the screen: out of the layout, and back down among the resting HUDs.
    private bubbleClosed = () => {
        SpeechBubbleLayout.closed(this)
        this.bubbleOrder = 0
        this.bubbleMoveEase = undefined
        this.liftOverlay(false)
    }

    // ---------------------------------------------------------------------------------------
    // The panel itself, and where the layout puts it (see speech_bubble_layout.ts).
    // ---------------------------------------------------------------------------------------

    private bubbleXFor = (side: BubbleSide) =>
        side === "right" ? BUBBLE_X : HEAD_CENTER_X * 2 - BUBBLE_X - this.bubbleW

    // Paint the panel at its current size and placement. Geometry only: the words and the Next button
    // are children laid out inside the body, so they ride along when it moves.
    //
    // The whole outline -- chamfered rect plus a tail on the bottom edge -- is ONE continuous path.
    // Filling and stroking a single path keeps the border seamless around the tail and the fill
    // uniform (drawing the tail as a separate shape left a seam + colour gap).
    private drawBubbleFrame = () => {
        const g = this.chatBubble
        const w = this.bubbleW
        const h = this.bubbleH
        const { side, dx, lift } = this.placement
        const c = BUBBLE_CHAMFER
        // The tail's x positions are authored for a bubble on the speaker's right; on the left they
        // mirror, so the tail still reaches back toward the head.
        const tailX = (x: number) => (side === "right" ? x : w - x)
        const baseA = tailX(TAIL_BASE_RIGHT_X)
        const baseB = tailX(TAIL_BASE_LEFT_X)
        // The path runs right-to-left along the bottom edge, so it meets the rightmost base first.
        const tailEnter = Math.max(baseA, baseB)
        const tailLeave = Math.min(baseA, baseB)
        // A moved body slides, rises or drops; the tip stays over the speaker, so the tail reaches it.
        const tipX = tailX(TAIL_TIP_X) - dx
        const tipY = h + TAIL_DROP + lift
        // Which edge the tail leaves from: the bottom, normally; the speaker-facing side once a drop
        // has brought the body down level with the tip.
        const facingX = side === "right" ? 0 : w
        const tail = tipY >= h + MIN_TAIL_DROP ? "bottom" : "side"
        const sideMid = Math.min(Math.max(tipY, c + SIDE_TAIL_HALF), h - c - SIDE_TAIL_HALF)
        const sideTipX = side === "right" ? Math.min(tipX, -SIDE_TAIL_LEN) : Math.max(tipX, w + SIDE_TAIL_LEN)
        const tracePath = (oy: number) => {
            g.moveTo(c, oy)
            g.lineTo(w - c, oy)
            g.lineTo(w, oy + c)
            if (tail === "side" && side === "left") {
                // down the right edge, out to the tip and back
                g.lineTo(facingX, oy + sideMid - SIDE_TAIL_HALF)
                g.lineTo(sideTipX, oy + tipY)
                g.lineTo(facingX, oy + sideMid + SIDE_TAIL_HALF)
            }
            g.lineTo(w, oy + h - c)
            g.lineTo(w - c, oy + h)
            if (tail === "bottom") {
                g.lineTo(tailEnter, oy + h)
                g.lineTo(tipX, oy + tipY) // out to the tail tip, back down toward the speaker
                g.lineTo(tailLeave, oy + h)
            }
            g.lineTo(c, oy + h)
            g.lineTo(0, oy + h - c)
            if (tail === "side" && side === "right") {
                // up the left edge, out to the tip and back
                g.lineTo(facingX, oy + sideMid + SIDE_TAIL_HALF)
                g.lineTo(sideTipX, oy + tipY)
                g.lineTo(facingX, oy + sideMid - SIDE_TAIL_HALF)
            }
            g.lineTo(0, oy + c)
            g.closePath()
        }

        g.clear()
        g.x = this.bubbleXFor(side) + dx
        g.y = this.bubbleRestY - lift

        // Dropped silhouette (same path, no stroke) so the panel sits ON the world, not in it
        g.lineStyle(0)
        g.beginFill(0x000000, BUBBLE_SHADOW_ALPHA)
        tracePath(BUBBLE_SHADOW_Y)
        g.endFill()

        // Panel body + tail: one path, filled with the dark edge stroke...
        g.lineStyle({
            width: BUBBLE_EDGE_WIDTH,
            color: TIMBER_EDGE,
            alpha: BUBBLE_EDGE_ALPHA,
            join: LINE_JOIN.MITER,
            miterLimit: BUBBLE_MITER_LIMIT,
        })
        g.beginFill(BUBBLE_FILL, BUBBLE_FILL_ALPHA)
        tracePath(0)
        g.endFill()
        // ...then the bronze rim down the middle of it
        g.lineStyle({
            width: BUBBLE_RIM_WIDTH,
            color: HUD_PANEL_RIM,
            alpha: 1,
            join: LINE_JOIN.MITER,
            miterLimit: BUBBLE_MITER_LIMIT,
        })
        tracePath(0)
        // One lit edge along the top, so the panel has a direction the light falls from (build_board)
        g.lineStyle(1, RIM_LIGHT, BUBBLE_TOP_LIGHT_ALPHA)
        g.moveTo(c + 1, 2.5)
        g.lineTo(w - c - 1, 2.5)
        g.lineStyle(0)
    }

    // This frame's slot in the bubble layout: the body on either side of the speaker, in a unit space
    // every HUD shares (HUD units, shifted by this HUD's on-screen origin), plus the visible screen
    // in those units. Null while there is no line up or the entity is not being drawn.
    bubbleSlot = (screen?: BubbleRect): BubbleSlot | null => {
        if (!this.bubbleOrder || !this.chatBubble.visible || !this.worldVisible) {
            return null
        }
        const origin = this.toGlobal(LAYOUT_ORIGIN, layoutOriginOut)
        const unit = this.toGlobal(LAYOUT_UNIT, layoutUnitOut)
        const scaleX = unit.x - origin.x
        const scaleY = unit.y - origin.y
        if (!(scaleX > 0) || !(scaleY > 0)) {
            return null
        }
        const x = origin.x / scaleX
        const y = origin.y / scaleY + this.bubbleRestY
        const w = this.bubbleW
        const h = this.bubbleH
        const rightX = x + this.bubbleXFor("right")
        const leftX = x + this.bubbleXFor("left")
        const baseMid = (TAIL_BASE_LEFT_X + TAIL_BASE_RIGHT_X) / 2
        const tipY = y + h + TAIL_DROP
        return {
            order: this.bubbleOrder,
            held: this.holding,
            fading: !!this.chatBubbleFader,
            right: { x: rightX, y, w, h },
            left: { x: leftX, y, w, h },
            tailBaseX: { right: rightX + baseMid, left: leftX + w - baseMid },
            tip: { right: { x: rightX + TAIL_TIP_X, y: tipY }, left: { x: leftX + w - TAIL_TIP_X, y: tipY } },
            current: this.placementTarget,
            head: {
                x: rightX - BUBBLE_X + HEAD_CENTER_X - HEAD_HALF_W,
                y: tipY - HEAD_ABOVE_TIP,
                w: HEAD_HALF_W * 2,
                h: HEAD_ABOVE_TIP + HEAD_BELOW_TIP,
            },
            bounds: screen && {
                x: screen.x / scaleX,
                y: screen.y / scaleY,
                w: screen.w / scaleX,
                h: screen.h / scaleY,
            },
        }
    }

    bubbleScreenRect = (): BubbleRect | null => {
        if (!this.bubbleOrder || !this.chatBubble.visible || !this.worldVisible || this.chatBubble.worldAlpha <= 0) {
            return null
        }
        const bounds = this.chatBubble.getBounds()
        return bounds.width > 0 && bounds.height > 0 ? { x: bounds.x, y: bounds.y, w: bounds.width, h: bounds.height } : null
    }

    applyBubblePlacement = (placement: BubblePlacement, paintRank: number, now: number) => {
        this.liftOverlay(true, paintRank)
        const fresh = this.placementFresh
        this.placementFresh = false
        const target = this.placementTarget
        if (placement.side === target.side && Math.abs(placement.dx - target.dx) < 1 &&
            Math.abs(placement.lift - target.lift) < 1) {
            return
        }
        this.placementTarget = placement
        if (fresh || placement.side !== this.placement.side) {
            // A line nobody has seen yet, or a change of side -- which could only be animated by
            // sliding the panel across the speaker's face: go straight there.
            this.bubbleMoveEase = undefined
            this.placement = placement
            this.drawBubbleFrame()
            return
        }
        this.bubbleMoveEase = { from: this.placement, to: placement, startedAt: now }
    }

    // Slide/rise toward the target, over a fixed BUBBLE_MOVE_MS whatever the distance.
    animateBubblePlacement = (now: number) => {
        const ease = this.bubbleMoveEase
        if (!ease) {
            return
        }
        const t = Math.min(1, Math.max(0, (now - ease.startedAt) / BUBBLE_MOVE_MS))
        const k = 1 - Math.pow(1 - t, 3)
        const dx = t >= 1 ? ease.to.dx : ease.from.dx + (ease.to.dx - ease.from.dx) * k
        const lift = t >= 1 ? ease.to.lift : ease.from.lift + (ease.to.lift - ease.from.lift) * k
        if (t >= 1) {
            this.bubbleMoveEase = undefined
        } else if (Math.abs(dx - this.placement.dx) < 0.25 && Math.abs(lift - this.placement.lift) < 0.25) {
            return // not a visible step; skip the redraw
        }
        this.placement = { side: this.placement.side, dx, lift }
        this.drawBubbleFrame()
    }

    // ---------------------------------------------------------------------------------------
    // The "Next" button on a held bubble.
    // ---------------------------------------------------------------------------------------

    private makeNextButton = (): UiButton => {
        const button = createButton({
            label: "Next ›", theme: "ember", shape: "chamfer", size: "small", bold: true,
            width: NEXT_BUTTON_W, height: NEXT_BUTTON_H, activation: "press",
            onClick: () => {
                // Suppress the document-level world attack before advancing the held line.
                SpeechHoldButtons.notePressed()
                this.advanceHeldSpeech()
            },
        })
        // The bubble owns the countdown; UiButton owns its label, plate, and input behavior.
        const progress = new Graphics()
        button.addChild(progress)
        this.nextButtonProgress = progress
        return button
    }

    private drawNextButton = () => {
        const pending = this.speechQueue.length
        this.nextButton?.setLabel(pending > 0 ? `Next ${pending + 1}›` : "Next ›")
    }

    private showNextButton = (bubbleW: number, bubbleH: number) => {
        if (!this.nextButton) {
            this.nextButton = this.makeNextButton()
        }
        const button = this.nextButton
        // Bottom-right inside the panel; the tail hangs off the bottom-LEFT, so the two never meet.
        button.x = bubbleW - BUBBLE_PAD_X - NEXT_BUTTON_W
        button.y = bubbleH - BUBBLE_PAD_Y - NEXT_BUTTON_H
        button.visible = true
        button.resetInteraction()
        this.drawNextButton()

        if (button.parent !== this.chatBubble) {
            this.chatBubble.addChild(button)
        }
        // Only a button the player can actually reach is registered -- the registry is what the
        // follow-cam hides while it composites the overlay into its texture.
        SpeechHoldButtons.register(button)

        this.armNextAutoAdvance()
    }

    // Start this line's countdown. When it runs out the button takes exactly the path a press takes
    // -- same ack, same backlog cycling -- so the speaker cannot tell the two apart, and a scene
    // paced off the ack keeps moving whether or not anyone is watching it.
    private armNextAutoAdvance = () => {
        this.clearNextAutoAdvance()
        this.nextArmedAt = Date.now()
        this.drawNextProgress()
        this.nextProgressInterval = setInterval(this.drawNextProgress, NEXT_PROGRESS_TICK_MS)
        this.nextAutoTimeout = setTimeout(() => {
            this.nextAutoTimeout = undefined
            this.advanceHeldSpeech()
        }, NEXT_BUTTON_AUTO_MS)
    }

    private clearNextAutoAdvance = () => {
        if (this.nextAutoTimeout) {
            clearTimeout(this.nextAutoTimeout)
            this.nextAutoTimeout = undefined
        }
        if (this.nextProgressInterval) {
            clearInterval(this.nextProgressInterval)
            this.nextProgressInterval = undefined
        }
        this.nextButtonProgress?.clear()
        this.nextProgressWidth = -1
    }

    // The countdown, as a thin gold bar along the bottom inside edge of the button that drains from
    // full to nothing. Time is read off the clock rather than counted in ticks so a throttled
    // background tab (or a dropped frame) leaves the bar honest about the timeout it is tracking.
    private drawNextProgress = () => {
        const bar = this.nextButtonProgress
        if (!bar) {
            return
        }
        const remaining = 1 - (Date.now() - this.nextArmedAt) / NEXT_BUTTON_AUTO_MS
        const full = NEXT_BUTTON_W - NEXT_PROGRESS_INSET * 2
        const width = Math.max(0, Math.round(full * Math.min(1, remaining)))
        if (width === this.nextProgressWidth) {
            return
        }
        this.nextProgressWidth = width
        bar.clear()
        if (width <= 0) {
            return
        }
        bar.beginFill(NEXT_PROGRESS_COLOR, NEXT_PROGRESS_ALPHA)
        bar.drawRect(NEXT_PROGRESS_INSET, NEXT_BUTTON_H - NEXT_PROGRESS_ROW, width, NEXT_PROGRESS_H)
        bar.endFill()
    }

    private hideNextButton = () => {
        this.clearNextAutoAdvance()
        const button = this.nextButton
        if (!button) {
            return
        }
        SpeechHoldButtons.unregister(button)
        button.visible = false
        button.resetInteraction()
        button.parent?.removeChild(button)
    }

    // The entity is going away (removed, or culled and discarded). Drop the HUD's own timers and,
    // above all, take any live Next button out of the shared registry -- a button left in there
    // belongs to a HUD nobody can click any more, and the follow-cam would keep toggling it forever.
    cleanup() {
        this.stopBubbleFade()
        SpeechBubbleLayout.closed(this)
        this.bubbleOrder = 0
        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (this.pointerIconPulseInterval) {
            clearInterval(this.pointerIconPulseInterval)
            this.pointerIconPulseInterval = undefined
        }
        if (this.pointerIconAngleInterval) {
            clearInterval(this.pointerIconAngleInterval)
            this.pointerIconAngleInterval = undefined
        }
        this.hideNextButton()
        this.speechQueue = []
        this.holding = false
        this.currentHeldMessage = undefined
        this.holdDone = undefined
        // Deliberately NOT fired: the HUD is being discarded, which is not the player reading
        // anything. A speaker waiting on the ack falls back to its own timeout instead.
        this.heldDismissedCallback = undefined
    }

    onMouseOver = (show: boolean) => {
        if (!this.suppressDetailHover && !this.speaking) {
            if (!this.alwaysShowLabel) {
                this.idText.visible = show
                this.hpBar.visible = show
                this.fadeGlyphTo(show ? 0 : 1)
            }

            if (show) {
                this.calcLabel()
            }
        }
    }

    updatePointerIcon(show: boolean, angle?: number) {
        if (!show) {
            clearInterval(this.pointerIconPulseInterval)
            clearInterval(this.pointerIconAngleInterval)
            this.pointerIconPulseInterval = undefined
            this.pointerIconAngleInterval = undefined
            this.pointerIcon.visible = false
            this.pointerIcon.angle = 0
        } else if (angle !== undefined) {
            this.pointerIcon.visible = true

            this.pointerIcon.angle = angle
            const radius = 32
            const x = radius * Math.sin((Math.PI * 2 * 90) / 360)
            const y = radius * Math.cos((Math.PI * 2 * 90) / 360)
            this.pointerIcon.x = x + radius - 10
            this.pointerIcon.y = y + radius + 10

            if (!this.pointerIconPulseInterval) {
                let scale = 1.0
                let scaleDir = 1

                this.pointerIconPulseInterval = setInterval(() => {
                    scale += 0.01 * scaleDir
                    if (scale > 1.2 || scale < 1.0) {
                        scaleDir = scaleDir * -1
                    }
                    this.pointerIcon.scale.set(scale)
                }, 50)
            }
        }
    }
}
